import { Component, ViewEncapsulation, ViewChild, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';

export interface StatCard {
  icon: string;
  bg: string;
  number: string | number;
  label: string;
}

export interface Project {
  name: string;
  status: 'approved' | 'rejected' | 'pending';
  bidAmount: string;
  timeline: string;
  clientRemarks: string;
  progress?: number;
  title?: string;
  revisionRequested?: boolean;
  revisedBudget?: number;
}

interface ApiProject {
  invited: boolean;
  assigned: boolean;
  rejected: boolean;
  totalAmount: number;
  pendingAmount: number;
  advancedAmount: number;
  startDate?: string;
  endDate?: string;
  title?: string;
  revision_requested?: boolean;
  revised_budget?: number;
}

interface ApiResponse {
  projects: {
    [key: string]: ApiProject;
  };
  totalBidsPlaced: number;
  bidsApproved: number;
  bidsRejected: number;
  pendingBids: number;
  onGoingProjects: number;
  totalEarnings: number;
  averageApproval: string;
  vendorCode: string;
  newBids?: number;
}

export interface VendorProject {
  id: string;
  customer: string;
  deliveryDate: string;
  createdDate: string;
  lead: string;
  type: string;
  status: string;
  acceptForBid: boolean;
  title: string;
  stgplan: boolean;
  stgres: boolean;
  stgdel: boolean;
  comment: string;
  owner: string;
  imagePath?: string;
  budget?: number;
  vendorCost?: number;
  price?: number;
  completed?: boolean;
  notifications?: any[];
  revisionRequested?: boolean;
  revisedBudget?: number;
}

@Component({
  selector: 'app-vendor-dashboard',
  templateUrl: './vendor-dashboard.component.html',
  styleUrls: ['./vendor-dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VendorDashboardComponent implements OnInit {
  searchQuery: string = '';
  filteredInvitedProjects: VendorProject[] = [];
filteredAssignedProjects: VendorProject[] = [];
filteredCompletedProjects: VendorProject[] = [];
filteredAllProjects: Project[] = [];
  loading: boolean = true;
  error: string | null = null;
  lastUpdated: Date | null = null;
  team: string = '';
  statsCards: StatCard[] = [
    {
      icon: 'assets/images/svgs/images/1_15.svg',
      bg: '#fdf2f8',
      number: 0,
      label: 'Total Bids Placed'
    },
    {
      icon: 'assets/images/svgs/images/1_25.svg',
      bg: '#f0fdf4',
      number: 0,
      label: 'Bids Approved'
    },
    {
      icon: 'assets/images/svgs/images/1_34.svg',
      bg: '#fef2f2',
      number: 0,
      label: 'Bids Rejected'
    },
    {
      icon: 'assets/images/svgs/images/1_44.svg',
      bg: '#f9fafb',
      number: 0,
      label: 'Pending Bids'
    },
    {
      icon: 'assets/images/svgs/images/1_53.svg',
      bg: '#eff6ff',
      number: 0,
      label: 'Ongoing Projects'
    },
    {
      icon: 'assets/images/svgs/images/1_62.svg',
      bg: '#fefce8',
      number: 0,
      label: 'Total Earnings'
    }
  ];

  projects: Project[] = [];
  newBids: number = 0;
  totalEarnings: string = '₹0';
  receivedAmount: string = '₹0';
  receivedPercentage: number = 0;
  pendingPercentage: number = 0;
  invitedProjects: VendorProject[] = [];
  assignedProjects: VendorProject[] = [];
  completedProjects: VendorProject[] = [];
  firstName = '';
  selectedStatus: string = 'All';
  statusOptions: string[] = ['All', 'In Progress', 'Completed', 'Abandoned'];
  isLoading: boolean = false;
  showNotifications: string | null = null;
  visibility: string = 'external';
  visibilityExternal: boolean = false;
  visibilityVendor: boolean = false;
  activeTab: string = 'all';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private http: HttpClient,
    private apiService: ApiService, 
    private dialog: MatDialog, 
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.fetchVendorData();
    this.initializeVendorProjects();
  }

  fetchVendorData(): void {
    this.loading = true;
    this.error = null;
    const contributorCode = this.getContributorCode();
    const url = `${environment.apiUrl}vendors/${contributorCode}`;
    
    this.http.get<ApiResponse>(url)
      .pipe(
        catchError(err => {
          this.error = 'Failed to load vendor data. Please try again later.';
          console.error('API Error:', err);
          return throwError(err);
        }),
        finalize(() => {
          this.loading = false;
          this.lastUpdated = new Date();
        })
      )
      .subscribe({
        next: (response) => this.processApiResponse(response)
      });
  }

  public calculatePendingAmount(): string {
    const numericValue = parseInt(this.totalEarnings.replace(/[^0-9]/g, ''), 10);
    const pendingAmount = (numericValue * this.pendingPercentage) / 100;
    return this.formatCurrency(pendingAmount, true);
  }

  public formatCurrency(amount: number, includeSymbol: boolean = true): string {
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
      currencyDisplay: 'symbol'
    });
    
    const formatted = formatter.format(amount);
    return includeSymbol ? formatted : formatted.replace('₹', '').trim();
  }

  private getContributorCode(): string {
    const userInfoString: string = localStorage.getItem('userInfo') || '{}';
    const userInfo = JSON.parse(userInfoString);
    const team = userInfo['custom:team'] || '';
    return team;
  }

  // private processApiResponse(response: ApiResponse): void {
  //   this.updateStatsCards(response);
  //   this.projects = this.transformProjects(response.projects);
  //   this.updateEarningsInfo(response);
  //   this.newBids = response.newBids !== undefined ? response.newBids : 0;
  // }

  private updateStatsCards(response: ApiResponse): void {
    this.statsCards = [
      { ...this.statsCards[0], number: response.totalBidsPlaced },
      { ...this.statsCards[1], number: response.bidsApproved },
      { ...this.statsCards[2], number: response.bidsRejected },
      { ...this.statsCards[3], number: response.pendingBids },
      { ...this.statsCards[4], number: response.onGoingProjects },
      { ...this.statsCards[5], number: this.formatCurrency(response.totalEarnings, false) },
    ];
  }

  private transformProjects(apiProjects: { [key: string]: ApiProject }): Project[] {
    return Object.entries(apiProjects).map(([name, project]) => ({
      name: this.formatProjectName(name),
      title: project.title,
      status: this.determineProjectStatus(project),
      bidAmount: this.formatCurrency(project.totalAmount),
      timeline: this.calculateTimeline(project),
      clientRemarks: this.getClientRemarks(this.determineProjectStatus(project)),
      progress: project.assigned ? this.calculateProjectProgress(project) : undefined,
      revisionRequested: !!(project.revision_requested),
      revisedBudget: project.revised_budget,
    }))
    .reverse();
  }

  private determineProjectStatus(project: ApiProject): 'approved' | 'rejected' | 'pending' {
    if (project.rejected) return 'rejected';
    if (project.assigned) return 'approved';
    return 'pending';
  }

  private calculateTimeline(project: ApiProject): string {
    if (!project.startDate || !project.endDate) return 'Not specified';
    
    const start = new Date(project.startDate);
    const end = new Date(project.endDate);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    return `${diffDays} days (${this.formatDate(start)} - ${this.formatDate(end)})`;
  }

  private calculateProjectProgress(project: ApiProject): number {
    if (!project.startDate || !project.endDate) return 0;
    
    const start = new Date(project.startDate).getTime();
    const end = new Date(project.endDate).getTime();
    const now = new Date().getTime();
    
    if (now >= end) return 100;
    if (now <= start) return 0;
    
    return Math.round(((now - start) / (end - start)) * 100);
  }

  private updateEarningsInfo(response: ApiResponse): void {
    this.totalEarnings = this.formatCurrency(response.totalEarnings);
    
    const totalPending = Object.values(response.projects)
      .reduce((sum, p) => sum + p.pendingAmount, 0);
    const totalReceived = response.totalEarnings - totalPending;
    
    this.receivedAmount = this.formatCurrency(totalReceived);
    this.receivedPercentage = Math.round((totalReceived / response.totalEarnings) * 100) || 0;
    this.pendingPercentage = 100 - this.receivedPercentage;
  }

  private formatProjectName(name: string): string {
    return name.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  private getClientRemarks(status: string): string {
    switch(status) {
      case 'approved': return 'Project approved!';
      case 'rejected': return 'Project rejected';
      default: return 'Pending client review';
    }
  }

  // public onSearch(): void {
  //   console.log('Searching for:', this.searchQuery);
  // }

  // public getStatusClass(status: string): string {
  //   return `status-${status}`;
  // }
  // Add this method to your VendorDashboardComponent class
getStatusText(status: string): string {
  // If status is 'rejected', show 'declined'
  if (status.toLowerCase() === 'rejected') {
    return 'declined';
  }
  // For all other statuses, return as-is
  return status;
}

  public onPortfolioView(): void {
    console.log('Navigating to portfolio view');
  }

  public onRefresh(): void {
    this.fetchVendorData();
  }

  // public get filteredProjects(): Project[] {
  //   if (!this.searchQuery) {
  //     return this.projects;
  //   }
    
  //   const query = this.searchQuery.toLowerCase();
  //   return this.projects.filter(project => 
  //     project.name.toLowerCase().includes(query) ||
  //     project.status.toLowerCase().includes(query) ||
  //     project.clientRemarks.toLowerCase().includes(query)
  //   );
  // }

  slugify(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')        
      .replace(/[^\w\-]+/g, '');  
  }

  initializeVendorProjects(): void {
    const userInfoString = localStorage.getItem('userInfo');
    if (userInfoString) {
      const userInfo = JSON.parse(userInfoString);
      this.visibility = userInfo['custom:visibility'] || 'local';
      this.visibilityExternal = this.visibility === 'customer' || this.visibility === 'vendor';
      this.visibilityVendor = this.visibility === 'vendor';
    }
    this.fetchProjects();
  }

  fetchProjects(): void {
    this.isLoading = true;
    const userInfoString = localStorage.getItem('userInfo');
    if (!userInfoString) {
      console.error('User info not found in localStorage');
      this.isLoading = false;
      return;
    }

    const userInfo = JSON.parse(userInfoString);
    const userName = userInfo.name || 'Unknown User';
    this.firstName = userInfo.name.split(' ')[0].toUpperCase();
    const visibility = userInfo['custom:visibility'] || 'local';
    const team = userInfo['custom:team'] || 'Unknown Team';
    const email = userInfo.email || 'Unknown Email';

    const apiUrl = `projects?user=${encodeURIComponent(userName)}&visibility=${encodeURIComponent(visibility)}&team=${encodeURIComponent(team)}&email=${encodeURIComponent(email)}`;

    this.apiService.get(apiUrl).subscribe(
      (response: any) => {
        const parsedData = response;
        this.categorizeProjectsForVendor(parsedData);
        this.isLoading = false;
      },
      (error) => {
        console.error('Error fetching projects:', error);
        this.isLoading = false;
        this.snackBar.open('Error fetching projects', 'Close', { duration: 3000 });
      }
    );
  }

  // private categorizeProjectsForVendor(projects: any[]): void {
  //   this.invitedProjects = [];
  //   this.assignedProjects = [];
  //   this.completedProjects = [];

  //   const userInfoString = localStorage.getItem('userInfo');
  //   if (!userInfoString) {
  //     console.error('User info not found');
  //     return;
  //   }

  //   const userInfo = JSON.parse(userInfoString);
  //   const team = userInfo['custom:team'];
  //   const userName = userInfo.name;

  //   const sortedData = projects.sort((a: any, b: any) => {
  //     const dateA = new Date(a.createdDate).getTime();
  //     const dateB = new Date(b.createdDate).getTime();
  //     return dateB - dateA;
  //   });

  //   sortedData.forEach((item: any) => {
  //     const mappedProject = this.mapProject(item);
      
  //     const isInvited = item.invited && item.invited[team] !== undefined;
      
  //     const vendorAssignment = item.VendorAssingedDetail?.find(
  //       (v: any) => v.vendor_code === team
  //     );
  //     const isInVendorAssignedDetail = !!vendorAssignment;
  //     const vendorStatus = vendorAssignment?.vendor_status;

  //     if ((item.status === 'Completed' || item.status === 'Abandoned') && isInVendorAssignedDetail && vendorStatus === 'accepted') {
  //       this.completedProjects.push(mappedProject);
  //     } 
  //     else if (isInvited && isInVendorAssignedDetail && 
  //             (vendorStatus === 'pending' || vendorStatus === 'accepted')) {
  //       this.assignedProjects.push(mappedProject);
  //     } 
  //     else if (isInvited && !isInVendorAssignedDetail) {
  //       this.invitedProjects.push(mappedProject);
  //     }
  //   });
  // }

  private mapProject(item: any, team?: string): any {
    const invitedEntry = team && item.invited ? item.invited[team] : null;
    return {
      id: item.id,
      imagePath: 'assets/images/profile/user-1.jpg',
      type: item.type,
      title: item.title,
      deliveryDate: item.deliveryDate,
      customer: item.customer,
      createdDate: new Date(item.createdDate),
      lastUpdatedDate: new Date(item.currentDate).toLocaleDateString(),
      budget: item.budget,
      lead: item.lead || 'sales',
      stgplan: item.stgplan,
      stgres: item.stgres,
      stgdel: item.stgdel,
      createdBy: item.created_by,
      vendorCost: item.vendorCost,
      price: item.vendorCost + (item.vendorCost * .3),
      owner: item.owner?.name || 'unknown Owner',
      comment: item.comment || 'N/A',
      status: item.status || 'In Progress',
      completed: item.completed,
      acceptForBid: item.acceptForBid || false,
      notifications: item.notifications || [],
      revisionRequested: !!(invitedEntry?.revision_requested),
      revisedBudget: invitedEntry?.revised_budget ?? undefined,
    };
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  isToday(date: string | Date): boolean {
    const today = new Date();
    const d = new Date(date);
    return (
      today.getFullYear() === d.getFullYear() &&
      today.getMonth() === d.getMonth() &&
      today.getDate() === d.getDate()
    );
  }

  isYesterday(date: string | Date): boolean {
    const today = new Date();
    const d = new Date(date);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return (
      yesterday.getFullYear() === d.getFullYear() &&
      yesterday.getMonth() === d.getMonth() &&
      yesterday.getDate() === d.getDate()
    );
  }

  isLastWeek(date: string | Date): boolean {
    const today = new Date();
    const d = new Date(date);
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);
    return d > oneWeekAgo && d < today;
  }

  getVendorCostStars(vendorCost: number | undefined): number {
  if (!vendorCost || vendorCost < 100000) return 1;
  if (vendorCost < 200000) return 2;
  if (vendorCost > 200000) return 3;
  return 4;
}

  getFormattedDate(date: string | Date): string {
    const d = new Date(date);
    if (this.isToday(d)) {
      return 'Today';
    } else if (this.isYesterday(d)) {
      return 'Yesterday';
    } else if (this.isLastWeek(d)) {
      return 'Last Week';
    } else {
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
  }

  getDateColor(date: string | Date): string {
    const d = new Date(date);
    if (this.isToday(d)) return 'red';
    if (this.isYesterday(d)) return 'orange';
    if (this.isLastWeek(d)) return 'blue';
    return '#4a5565';
  }

  updateProjectStatus(projectId: string, newStatus: string) {
    this.apiService.patch(`projects/${projectId}/status`, { status: newStatus }).subscribe(
      () => {
        this.snackBar.open('Project status updated', 'Close', { duration: 2000 });
        this.fetchProjects();
      },
      (error) => {
        console.error('Error updating project status:', error);
        this.snackBar.open('Error updating status', 'Close', { duration: 3000 });
        this.fetchProjects();
      }
    );
  }

  deleteProject(projectId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '300px',
      data: {
        title: 'Delete Project',
        message: 'Are you sure you want to delete this project?',
        isConfirm: true,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        showButtons: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiService.delete(`projects/${projectId}`).subscribe(
          () => {
            this.fetchProjects();
            this.dialog.open(ConfirmDialogComponent, {
              width: '300px',
              data: {
                title: 'Success',
                message: 'Project deleted successfully!',
                showButtons: false,
                autoClose: 2000
              }
            });
          },
          (error) => {
            console.error('Error deleting project:', error);
            this.dialog.open(ConfirmDialogComponent, {
              width: '300px',
              data: {
                title: 'Error',
                message: 'Failed to delete project. Please try again.',
                isConfirm: false,
                showButtons: true
              }
            });
          }
        );
      }
    });
  }

searchAll(): void {
  const query = this.searchQuery.trim().toLowerCase();
  
  this.filteredInvitedProjects = this.invitedProjects.filter(project => 
    this.vendorProjectMatchesQuery(project, query)
  );
  
  this.filteredAssignedProjects = this.assignedProjects.filter(project => 
    this.vendorProjectMatchesQuery(project, query)
  );
  
  this.filteredCompletedProjects = this.completedProjects.filter(project => 
    this.vendorProjectMatchesQuery(project, query)
  );
  
  this.filteredAllProjects = this.projects.filter(project => 
    this.dashboardProjectMatchesQuery(project, query)
  );
}

private vendorProjectMatchesQuery(project: VendorProject, query: string): boolean {
  if (!query) return true;
  
  return (
    (project.title?.toLowerCase().includes(query)) ||
    (project.customer?.toLowerCase().includes(query)) ||
    (project.type?.toLowerCase().includes(query)) ||
    (project.owner?.toLowerCase().includes(query)) ||
    (project.status?.toLowerCase().includes(query)) ||
    (project.comment?.toLowerCase().includes(query)) ||
    (project.lead?.toLowerCase().includes(query)) ||
    (project.deliveryDate?.toLowerCase().includes(query))
  );
}

private dashboardProjectMatchesQuery(project: Project, query: string): boolean {
  if (!query) return true;
  
  return (
    (project.name?.toLowerCase().includes(query)) ||
    (project.status?.toLowerCase().includes(query)) ||
    (project.clientRemarks?.toLowerCase().includes(query)) ||
    (project.bidAmount?.toLowerCase().includes(query)) ||
    (project.timeline?.toLowerCase().includes(query))
  );
}

public onSearch(): void {
  console.log('Searching for:', this.searchQuery);
  this.searchAll();
}

public get filteredProjects(): Project[] {
  if (!this.searchQuery) {
    return this.projects;
  }
  return this.filteredAllProjects.length > 0 ? this.filteredAllProjects : 
         this.projects.filter(project => this.dashboardProjectMatchesQuery(project, this.searchQuery.toLowerCase()));
}

private categorizeProjectsForVendor(projects: any[]): void {
  this.invitedProjects = [];
  this.assignedProjects = [];
  this.completedProjects = [];

  const userInfoString = localStorage.getItem('userInfo');
  if (!userInfoString) {
    console.error('User info not found');
    return;
  }

  const userInfo = JSON.parse(userInfoString);
  const team = userInfo['custom:team'];
  const userName = userInfo.name;

  const sortedData = projects.sort((a: any, b: any) => {
    const dateA = new Date(a.createdDate).getTime();
    const dateB = new Date(b.createdDate).getTime();
    return dateB - dateA;
  });

  sortedData.forEach((item: any) => {
    const mappedProject = this.mapProject(item, team);

    const isInvited = item.invited && item.invited[team] !== undefined;
    
    const vendorAssignment = item.VendorAssingedDetail?.find(
      (v: any) => v.vendor_code === team
    );
    const isInVendorAssignedDetail = !!vendorAssignment;
    const vendorStatus = vendorAssignment?.vendor_status;

    if ((item.status === 'Completed' || item.status === 'Abandoned') && isInVendorAssignedDetail && vendorStatus === 'accepted') {
      this.completedProjects.push(mappedProject);
    } 
    else if (isInvited && isInVendorAssignedDetail && 
            (vendorStatus === 'pending' || vendorStatus === 'accepted')) {
      this.assignedProjects.push(mappedProject);
    } 
    else if (isInvited && !isInVendorAssignedDetail) {
      this.invitedProjects.push(mappedProject);
    }
  });

  // Re-bid required projects float to top of invited list
  this.invitedProjects.sort((a, b) => (b.revisionRequested ? 1 : 0) - (a.revisionRequested ? 1 : 0));
  this.filteredInvitedProjects = [...this.invitedProjects];
  this.filteredAssignedProjects = [...this.assignedProjects];
  this.filteredCompletedProjects = [...this.completedProjects];
}

private processApiResponse(response: ApiResponse): void {
  this.updateStatsCards(response);
  this.projects = this.transformProjects(response.projects);
  this.filteredAllProjects = [...this.projects];
  this.updateEarningsInfo(response);
  this.newBids = response.newBids !== undefined ? response.newBids : 0;
}
}